/*
  Gift wrap — chosen on the product page, charged in the bag.

  THE RULE
    - The product page box adds the line with "Gift: Yes" (a line property).
      Wrapped and unwrapped units of the same product are therefore separate
      lines, and the mark carries through to checkout and the order.
    - Shopify can only charge for products, so each marked unit also needs one
      "Gift Wrap" product in the bag (picked in the product page's Gift
      toggle block — the one place gift wrap is set up). That wrap line is hidden in the bag list and counted in the
      total.
    - The bag only REPORTS the choice. There is no checkbox there: to change
      it, the shopper removes the line and adds it again from the product page.

  WHAT THIS FILE DOES
    After every cart change (and on page load) it makes the wrap lines match
    the marked units: for each variant, wrap quantity = units of that variant
    carrying the Gift mark. That covers add, + / −, delete and an emptied bag
    in one place — nothing else adds or removes a wrap.

  Wrap lines carry _tyo_wrap_for = the variant id they belong to (hidden at
  checkout because it starts with an underscore).
*/

const tyoWrapConfig = () => window.tyoGiftWrap || {};

/*
  The drawer's own refreshFromServer calls open() when it finishes, which is
  right for an add the shopper asked for and wrong here: it would replay the
  slide-in on an open drawer, or throw a closed one open. So the two sections
  are swapped in quietly.
*/
async function silentRefresh() {
  const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
  if (!response.ok) return;
  const sections = await response.json();

  const parse = (html, selector) =>
    new DOMParser().parseFromString(html || '', 'text/html').querySelector(selector);

  const bubble = document.getElementById('cart-icon-bubble');
  const freshBubble = parse(sections['cart-icon-bubble'], '.shopify-section');
  if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;

  const drawer = document.getElementById('CartDrawer');
  const freshDrawer = parse(sections['cart-drawer'], '#CartDrawer');
  if (drawer && freshDrawer) drawer.innerHTML = freshDrawer.innerHTML;

  document
    .querySelector('cart-drawer')
    ?.classList.toggle('is-empty', !!document.querySelector('.drawer__inner-empty'));
}

const readCart = async () =>
  (
    await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
  ).json();

// '' for an ordinary line, the wrapped variant id for a wrap line.
const wrapTarget = (line) => String((line.properties && line.properties._tyo_wrap_for) || '');

const postJSON = (url, body) => fetch(url, { ...fetchConfig('javascript'), body: JSON.stringify(body) });

/* ------------------------------------------------- keeping wraps right */

let wrapSyncRunning = false;
let wrapSyncAgain = false;

async function syncWraps() {
  const { key: giftKey = 'Gift' } = tyoWrapConfig();

  // A change that arrives mid-run is queued, and the run repeats until right.
  if (wrapSyncRunning) {
    wrapSyncAgain = true;
    return;
  }
  wrapSyncRunning = true;

  try {
    do {
      wrapSyncAgain = false;
      const cart = await readCart();

      // How many units of each variant were added with the Gift mark.
      const wanted = new Map();
      cart.items.forEach((line) => {
        if (wrapTarget(line) || !line.properties || !line.properties[giftKey]) return;
        const id = String(line.variant_id);
        wanted.set(id, (wanted.get(id) || 0) + line.quantity);
      });

      const wrapLines = cart.items.filter((line) => wrapTarget(line));
      let changed = false;

      /*
        Which product is the wrap: the one picked in the product page's Gift
        toggle block (on its checkbox), else the one already in the bag. Off
        the product page with no wrap in the bag there is nothing to add from,
        and that is fine — wraps are only ever started on the product page.
      */
      const wrapVariant =
        document.querySelector('[data-tyo-wrap-variant]')?.dataset.tyoWrapVariant ||
        (wrapLines[0] && wrapLines[0].variant_id);

      // Existing wraps: set to the marked units, or 0 once those are gone.
      for (const line of wrapLines) {
        const want = wanted.get(wrapTarget(line)) || 0;
        if (line.quantity !== want) {
          await postJSON(window.routes.cart_change_url, { id: line.key, quantity: want });
          changed = true;
        }
        wanted.delete(wrapTarget(line));
      }

      // Marked units with no wrap line yet: add one line per variant.
      const missing = [...wanted.entries()].filter(([, quantity]) => quantity > 0);
      if (missing.length && wrapVariant) {
        await postJSON(window.routes.cart_add_url, {
          items: missing.map(([target, quantity]) => ({
            id: Number(wrapVariant),
            quantity,
            properties: { _tyo_wrap_for: target },
          })),
        });
        changed = true;
      }

      if (changed) await silentRefresh();
    } while (wrapSyncAgain);
  } catch (error) {
    console.error('[cart-giftwrap] could not bring the gift wrap in line with the bag', error);
  } finally {
    wrapSyncRunning = false;
  }
}

// Rendered inside the drawer, so every drawer render triggers a sync.
class GiftWrapSweeper extends HTMLElement {
  connectedCallback() {
    syncWraps();
  }
}

if (!customElements.get('gift-wrap-sweeper')) customElements.define('gift-wrap-sweeper', GiftWrapSweeper);

/*
  For checkout (assets/shopflo-checkout-tyohaar.js): resolves once the wrap
  lines match the bag — waiting out a sync already running, then running one
  more — so the checkout never opens with a wrap missing or stale.
*/
// Re-draws the bag and its count without opening the drawer — used when Buy it
// now puts the shopper's saved cart back (assets/shopflo-checkout-tyohaar.js).
window.tyoCartRefresh = silentRefresh;

window.tyoSyncWraps = async () => {
  while (wrapSyncRunning) await new Promise((resolve) => setTimeout(resolve, 100));
  await syncWraps();
  while (wrapSyncRunning) await new Promise((resolve) => setTimeout(resolve, 100));
};

// Every cart change from any source: product page add, + / −, delete.
if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => syncWraps());
}
